import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, get, set } from 'firebase/database';
import Swal from 'sweetalert2';
import {
  TrashIcon,
  PlusIcon,
  MinusIcon,
  ShoppingBagIcon,
  LockClosedIcon,
  ArrowRightIcon,
  XMarkIcon,
  TagIcon,
  SparklesIcon,
  ExclamationTriangleIcon,
  UserCircleIcon,
  ShoppingCartIcon,
  ChevronRightIcon,
  GiftIcon,
  TruckIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { CheckBadgeIcon, FireIcon } from '@heroicons/react/24/solid';

// ─── SWEETALERT Z-INDEX FIX ──────────────────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('swal-cart-z')) {
  const s = document.createElement('style');
  s.id = 'swal-cart-z';
  s.textContent = `.swal2-container { z-index: 999999 !important; } .swal2-on-top { z-index: 999999 !important; }`;
  document.head.appendChild(s);
}
const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2500, toast: true, position: 'top-end', showConfirmButton: false, customClass: { container: 'swal2-on-top' } });

// ─── LOCAL STORAGE HELPERS ───────────────────────────────────────────────────
const LS = {
  get:    (key, fb = null) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fb; } catch { return fb; } },
  set:    (key, val)       => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} },
  remove: (key)            => { try { localStorage.removeItem(key); } catch {} },
};

// ─── PRICE HELPERS ───────────────────────────────────────────────────────────
const isOfferActive = (offer) => {
  if (!offer || !offer.isActive) return false;
  const now = Date.now();
  if (!offer.startDate && !offer.endDate) return true;
  if (offer.startDate && offer.endDate)   return now >= offer.startDate && now <= offer.endDate;
  if (offer.startDate && !offer.endDate)  return now >= offer.startDate;
  if (!offer.startDate && offer.endDate)  return now <= offer.endDate;
  return true;
};

// Resolve what a cart item should display. Cart items already store the
// resolved price at time of adding, but we re-check in case offer/membership
// changed while items were in cart.
const resolveCartItemPrice = (item, isMember) => {
  // If the item has a live product reference we could re-check, but cart
  // items store price, originalPrice, memberPrice, offerPrice at add-time.
  // Show the stored resolved price; originalPrice for strikethrough.
  const showStrike = item.originalPrice && item.originalPrice > item.price;
  return { displayPrice: item.price, originalPrice: showStrike ? item.originalPrice : null };
};

// ─── EMPTY CART STATE ────────────────────────────────────────────────────────
const EmptyCart = () => {
  const navigate = useNavigate();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-24 px-6 text-center"
    >
      <div className="relative mb-8">
        <div className="w-32 h-32 rounded-full flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg,#fef2f2,#fee2e2)' }}>
          <ShoppingCartIcon className="h-16 w-16" style={{ color: '#fca5a5' }} />
        </div>
        <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg"
          style={{ background: '#800020' }}>0</div>
      </div>
      <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Your cart is empty</h2>
      <p className="text-gray-500 text-sm mb-8 max-w-xs leading-relaxed">
        Looks like you haven't added anything yet. Explore our products and find something you love.
      </p>
      <motion.button
        whileHover={{ scale: 1.04, boxShadow: '0 8px 28px rgba(128,0,32,0.35)' }}
        whileTap={{ scale: 0.97 }}
        onClick={() => navigate('/shop')}
        className="inline-flex items-center gap-2 px-8 py-3.5 rounded-2xl text-sm font-bold text-white shadow-lg"
        style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
        <SparklesIcon className="h-4 w-4" />
        Browse Products
        <ArrowRightIcon className="h-4 w-4" />
      </motion.button>
    </motion.div>
  );
};

// ─── LOGIN GATE BANNER ───────────────────────────────────────────────────────
const LoginGateBanner = () => {
  const navigate = useNavigate();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl overflow-hidden mb-6"
      style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#800020 100%)' }}
    >
      <div className="p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(255,255,255,0.12)' }}>
          <LockClosedIcon className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-base">Sign in to checkout</p>
          <p className="text-white/65 text-sm mt-0.5 leading-snug">
            You're shopping as a guest. Your cart is saved locally — sign in to place your order, track it, and unlock member prices.
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}
          onClick={() => navigate('/login', { state: { from: '/cart' } })}
          className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all"
          style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.3)' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
        >
          <UserCircleIcon className="h-4 w-4" />
          Sign In
          <ChevronRightIcon className="h-3.5 w-3.5" />
        </motion.button>
      </div>
      {/* Subtle pulsing dot */}
      <div className="px-5 pb-4 flex items-center gap-2">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-300 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400" />
        </span>
        <span className="text-white/50 text-xs">Cart contents are stored on this device only until you sign in</span>
      </div>
    </motion.div>
  );
};

// ─── CART ITEM ROW ───────────────────────────────────────────────────────────
const CartItem = ({ item, isMember, onIncrement, onDecrement, onRemove }) => {
  const { displayPrice, originalPrice } = resolveCartItemPrice(item, isMember);
  const lineTotal = displayPrice * item.quantity;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20, height: 0, marginBottom: 0 }}
      transition={{ duration: 0.25 }}
      className="flex gap-4 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm"
    >
      {/* Image */}
      <div className="w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 flex items-center justify-center">
        {item.image
          ? <img src={item.image} alt={item.name} className="w-full h-full object-contain p-1.5" />
          : <ShoppingBagIcon className="h-8 w-8 text-gray-200" />
        }
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-900 leading-tight line-clamp-2">{item.name}</p>

        {/* Price */}
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <span className="text-sm font-extrabold" style={{ color: '#800020' }}>
            KES {displayPrice?.toLocaleString()}
          </span>
          {originalPrice && (
            <span className="text-xs text-gray-400 line-through">KES {originalPrice?.toLocaleString()}</span>
          )}
          {item.price < item.originalPrice && (
            <span className="text-[10px] font-bold text-white px-1.5 py-0.5 rounded-md" style={{ background: '#dc2626' }}>
              SALE
            </span>
          )}
          {isMember && item.memberPrice && item.memberPrice === item.price && item.memberPrice < item.originalPrice && (
            <span className="text-[10px] font-bold text-white px-1.5 py-0.5 rounded-md" style={{ background: '#800020' }}>
              MEMBER
            </span>
          )}
        </div>

        {/* Qty controls + line total */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-1 rounded-xl border border-gray-200 overflow-hidden">
            <button
              onClick={() => onDecrement(item)}
              className="w-8 h-8 flex items-center justify-center transition-colors hover:bg-gray-100"
              style={{ color: item.quantity <= 1 ? '#d1d5db' : '#374151' }}
              disabled={item.quantity <= 1}
            >
              <MinusIcon className="h-3.5 w-3.5" />
            </button>
            <span className="px-3 text-sm font-bold text-gray-800 min-w-[2rem] text-center">{item.quantity}</span>
            <button
              onClick={() => onIncrement(item)}
              className="w-8 h-8 flex items-center justify-center transition-colors hover:bg-gray-100 text-gray-600"
            >
              <PlusIcon className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-gray-700">KES {lineTotal?.toLocaleString()}</span>
            <button
              onClick={() => onRemove(item)}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all"
              title="Remove item"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ─── ORDER SUMMARY ───────────────────────────────────────────────────────────
const OrderSummary = ({ cart, isMember, onCheckout, isLoggedIn, onLogin }) => {
  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const savings  = cart.reduce((sum, item) => {
    const orig = item.originalPrice || item.price;
    return sum + (orig - item.price) * item.quantity;
  }, 0);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden sticky top-6">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100" style={{ background: 'linear-gradient(135deg,#f8f9fa,#fef2f2)' }}>
        <h3 className="text-base font-extrabold text-gray-900">Order Summary</h3>
        <p className="text-xs text-gray-400 mt-0.5">{cart.reduce((s, i) => s + i.quantity, 0)} item{cart.reduce((s, i) => s + i.quantity, 0) !== 1 ? 's' : ''}</p>
      </div>

      <div className="p-6 space-y-4">
        {/* Line items summary */}
        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
          {cart.map(item => (
            <div key={item.id} className="flex items-center justify-between text-sm">
              <span className="text-gray-600 truncate max-w-[60%]">{item.name} <span className="text-gray-400">×{item.quantity}</span></span>
              <span className="font-semibold text-gray-800 flex-shrink-0 ml-2">KES {(item.price * item.quantity)?.toLocaleString()}</span>
            </div>
          ))}
        </div>

        <div className="border-t border-dashed border-gray-200 pt-4 space-y-2.5">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Subtotal</span>
            <span className="font-semibold text-gray-800">KES {subtotal?.toLocaleString()}</span>
          </div>

          {savings > 0 && (
            <div className="flex justify-between text-sm">
              <span className="flex items-center gap-1.5 text-green-600">
                <GiftIcon className="h-3.5 w-3.5" />
                Savings
              </span>
              <span className="font-bold text-green-600">- KES {savings?.toLocaleString()}</span>
            </div>
          )}

          <div className="flex justify-between text-sm">
            <span className="flex items-center gap-1.5 text-gray-500">
              <TruckIcon className="h-3.5 w-3.5" />
              Delivery
            </span>
            <span className="text-gray-500 italic text-xs">Calculated at checkout</span>
          </div>
        </div>

        {/* Total */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex justify-between items-center">
            <span className="text-base font-extrabold text-gray-900">Total</span>
            <div className="text-right">
              <p className="text-xl font-extrabold" style={{ color: '#800020' }}>KES {subtotal?.toLocaleString()}</p>
              {savings > 0 && <p className="text-xs text-green-600 font-medium">You save KES {savings?.toLocaleString()}</p>}
            </div>
          </div>
        </div>

        {/* Trust badges */}
        <div className="flex items-center gap-3 py-3 px-4 rounded-xl" style={{ background: '#f8fafb' }}>
          <ShieldCheckIcon className="h-5 w-5 text-green-500 flex-shrink-0" />
          <p className="text-xs text-gray-500 leading-snug">Secure checkout. Your information is encrypted and protected.</p>
        </div>

        {/* CTA */}
        {isLoggedIn ? (
          <motion.button
            whileHover={{ scale: 1.02, boxShadow: '0 8px 24px rgba(128,0,32,0.35)' }}
            whileTap={{ scale: 0.97 }}
            onClick={onCheckout}
            className="w-full py-4 rounded-2xl text-white font-extrabold text-base flex items-center justify-center gap-2 shadow-lg transition-all"
            style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}
          >
            <LockClosedIcon className="h-4 w-4" />
            Proceed to Checkout
            <ArrowRightIcon className="h-4 w-4" />
          </motion.button>
        ) : (
          <div className="space-y-3">
            <button
              onClick={onLogin}
              className="w-full py-4 rounded-2xl text-white font-extrabold text-base flex items-center justify-center gap-2 shadow-lg transition-all"
              style={{ background: 'linear-gradient(135deg,#1a1a2e,#800020)' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <UserCircleIcon className="h-5 w-5" />
              Sign In to Checkout
            </button>
            <p className="text-center text-xs text-gray-400">
              Guest? <button onClick={onLogin} className="font-semibold underline" style={{ color: '#800020' }}>Create an account</button> to place your order
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN CART COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const Cart = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const isMember = !!(currentUser && userData?.membership?.status === 'active');

  const [cart,    setCart]    = useState([]);
  const [loading, setLoading] = useState(true);

  // ─── NAVIGATION HANDLERS ───────────────────────────────────────────────────
  const handleContinueShopping = () => {
    navigate('/shop');
  };

  const handleNavigateToLogin = () => {
    navigate('/login', { state: { from: '/cart' } });
  };

  const handleNavigateToCheckout = () => {
    navigate('/checkout');
  };

  // ─── LOAD CART ────────────────────────────────────────────────────────────
  const loadCart = useCallback(async () => {
    setLoading(true);
    try {
      if (currentUser) {
        // Logged in: always load from Firebase
        const cartRef  = ref(database, `users/${currentUser.uid}/cart`);
        const snap     = await get(cartRef);
        const fbCart   = snap.exists()
          ? (Array.isArray(snap.val()) ? snap.val() : Object.values(snap.val()))
          : [];
        setCart(fbCart);
      } else {
        // Guest: load from localStorage
        setCart(LS.get('shop_cart', []));
      }
    } catch (e) {
      console.error('Cart load error:', e);
      setCart(LS.get('shop_cart', []));
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { loadCart(); }, [loadCart]);

  // ─── SAVE CART ────────────────────────────────────────────────────────────
  const saveCart = async (updated) => {
    setCart(updated);
    if (currentUser) {
      try {
        await set(ref(database, `users/${currentUser.uid}/cart`), updated);
      } catch (e) { console.error('Cart save error:', e); }
    } else {
      LS.set('shop_cart', updated);
    }
  };

  // ─── INCREMENT ────────────────────────────────────────────────────────────
  const handleIncrement = (item) => {
    const updated = cart.map(i => i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i);
    saveCart(updated);
  };

  // ─── DECREMENT ────────────────────────────────────────────────────────────
  const handleDecrement = (item) => {
    if (item.quantity <= 1) return; // don't go below 1 — use remove for that
    const updated = cart.map(i => i.id === item.id ? { ...i, quantity: i.quantity - 1 } : i);
    saveCart(updated);
  };

  // ─── REMOVE ───────────────────────────────────────────────────────────────
  const handleRemove = async (item) => {
    const result = await Swal.fire({
      title: 'Remove item?',
      text: `Remove "${item.name}" from your cart?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#800020',
      cancelButtonColor: '#e5e7eb',
      confirmButtonText: 'Remove',
      cancelButtonText: 'Keep',
      customClass: { container: 'swal2-on-top' },
    });
    if (!result.isConfirmed) return;
    const updated = cart.filter(i => i.id !== item.id);
    saveCart(updated);
    toast('success', 'Item removed');
  };

  // ─── CLEAR CART ───────────────────────────────────────────────────────────
  const handleClearCart = async () => {
    const result = await Swal.fire({
      title: 'Clear entire cart?',
      text: 'All items will be removed. This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#800020',
      cancelButtonColor: '#e5e7eb',
      confirmButtonText: 'Clear Cart',
      cancelButtonText: 'Cancel',
      customClass: { container: 'swal2-on-top' },
    });
    if (!result.isConfirmed) return;
    await saveCart([]);
    toast('success', 'Cart cleared');
  };

  // ─── CHECKOUT ─────────────────────────────────────────────────────────────
  const handleCheckout = () => {
    if (!currentUser) {
      handleNavigateToLogin();
      return;
    }
    if (cart.length === 0) {
      toast('warning', 'Your cart is empty');
      return;
    }
    handleNavigateToCheckout();
  };

  // ─── DERIVED ──────────────────────────────────────────────────────────────
  const totalItems = cart.reduce((s, i) => s + i.quantity, 0);

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg,#f8f9fa 0%,#f1f3f5 100%)' }}>

      {/* ── PAGE HEADER ─────────────────────────────────────────────────── */}
      <div className="border-b border-gray-200 bg-white sticky top-0" style={{ zIndex: 40 }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
              <ShoppingCartIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-extrabold text-gray-900">Shopping Cart</h1>
              <p className="text-xs text-gray-400">
                {loading ? 'Loading…' : totalItems === 0 ? 'Empty' : `${totalItems} item${totalItems !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Member badge */}
            {isMember && (
              <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white"
                style={{ background: 'linear-gradient(135deg,#1a1a2e,#800020)' }}>
                <CheckBadgeIcon className="h-3.5 w-3.5" />
                Member Prices Applied
              </span>
            )}
            {/* Continue shopping */}
            <button
              onClick={handleContinueShopping}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all"
              style={{ borderColor: '#800020', color: '#800020' }}
              onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <SparklesIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Continue Shopping</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ────────────────────────────────────────────────── */}
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-8">

        {loading ? (
          <div className="flex justify-center items-center py-32">
            <div className="w-10 h-10 border-4 border-red-200 border-t-red-700 rounded-full animate-spin" />
          </div>
        ) : cart.length === 0 ? (
          <EmptyCart />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

            {/* ── LEFT: Cart Items ─────────────────────────────────────── */}
            <div className="lg:col-span-2 space-y-4">

              {/* Login gate for guests */}
              {!currentUser && <LoginGateBanner />}

              {/* Member perks notice */}
              {currentUser && !isMember && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-3 p-4 rounded-2xl border"
                  style={{ background: '#fffbeb', borderColor: '#fde68a' }}>
                  <FireIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-amber-800">Unlock member prices</p>
                    <p className="text-xs text-amber-700 mt-0.5">Some items have lower member-only prices. Subscribe to a membership plan to save more.</p>
                    <button 
                      onClick={() => navigate('/pricing')}
                      className="text-xs font-semibold text-amber-700 underline mt-1"
                    >
                      View Membership Plans →
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Cart header row */}
              <div className="flex items-center justify-between px-1">
                <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                  {cart.length} Product{cart.length !== 1 ? 's' : ''}
                </h2>
                <button
                  onClick={handleClearCart}
                  className="flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:text-red-700 transition-colors px-3 py-1.5 rounded-lg hover:bg-red-50"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                  Clear All
                </button>
              </div>

              {/* Items list */}
              <AnimatePresence mode="popLayout">
                {cart.map(item => (
                  <CartItem
                    key={item.id}
                    item={item}
                    isMember={isMember}
                    onIncrement={handleIncrement}
                    onDecrement={handleDecrement}
                    onRemove={handleRemove}
                  />
                ))}
              </AnimatePresence>

              {/* Guest reminder at bottom of list */}
              {!currentUser && cart.length > 0 && (
                <motion.div
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="flex items-start gap-3 p-4 rounded-2xl border border-orange-200 mt-2"
                  style={{ background: '#fff7ed' }}>
                  <ExclamationTriangleIcon className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-orange-800">Cart not saved to your account</p>
                    <p className="text-xs text-orange-700 mt-0.5">
                      This cart is only saved on this device.{' '}
                      <button onClick={handleNavigateToLogin} className="underline font-semibold">Sign in</button>{' '}
                      to save your cart and proceed to checkout.
                    </p>
                  </div>
                </motion.div>
              )}
            </div>

            {/* ── RIGHT: Order Summary ─────────────────────────────────── */}
            <div className="lg:col-span-1">
              <OrderSummary
                cart={cart}
                isMember={isMember}
                onCheckout={handleCheckout}
                isLoggedIn={!!currentUser}
                onLogin={handleNavigateToLogin}
              />

              {/* Trust info below summary */}
              <div className="mt-4 grid grid-cols-2 gap-3">
                {[
                  { icon: <ShieldCheckIcon className="h-4 w-4" />, label: 'Secure Payment', sub: 'M-Pesa & Visa' },
                  { icon: <TruckIcon className="h-4 w-4" />, label: 'Fast Delivery', sub: 'Nairobi & beyond' },
                ].map(({ icon, label, sub }) => (
                  <div key={label} className="flex items-center gap-2.5 p-3 bg-white rounded-xl border border-gray-100 shadow-sm">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: '#fef2f2', color: '#800020' }}>
                      {icon}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-800">{label}</p>
                      <p className="text-[10px] text-gray-400">{sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .line-clamp-2 { display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden; }
      `}</style>
    </div>
  );
};

export default Cart;